import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faBooks } from '@fortawesome/pro-light-svg-icons';
import { select, Store, } from '@ngrx/store';
import { EditionData } from 'app/citation/models/editions';
import { DiscoverV2BffService } from 'app/elements-v2/service/discover-v2-bff.service';
import { Cover } from 'app/models/cover';
import { I18NService } from 'app/services/i18-n.service';
import { ShareItResource, ShareItShareEntityType } from 'app/share-it/models/share-it';
import { QueryHelper } from 'core/utils/query-helper';
import { merge } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';
import { map } from 'rxjs/operators';
import { ResetFacetBubblesAction, UpdateSearchObjectAction } from 'search/actions/facet.actions';
import { SearchResultsReset } from 'search/actions/search.actions';
import { FacetURLFilters } from 'search/facets/models/facet-state';
import { SearchType } from 'search/models/search-type';
import { getLockedFacetsAsQueryParams, getSearchResultsMetadataForScroll } from 'search/reducers/root.reducer';
import { BulkFeatureKey } from '../../../../bulk-select/enums/bulk-feature-key';
import { CustomerFeature } from '../../../../customer-integration/customer-integration';
import { CustomerFeatureToggleService } from '../../../../customer-integration/services/customer-feature-toggle.service';
import { Breakpoints } from '../../../../models/viewport';
import { DictionariesService } from '../../../../services/dictionaries.service';
import { FeatureToggleService } from '../../../../services/feature-toggle.service';
import { ViewportService } from '../../../../services/viewport.service';
import { resetFocusedResourceId, resetScrollAndSelectedResourceId, setSelectedResourceId } from '../../../actions/entity.actions';
import { Edition, EntityTypes, FormatGroup, GetItRequest, HoldRequest } from '../../../models/entity';
import { getFocusedResourceId, getSelectedResourceId, getSelectedRollupTabIndex } from '../../../reducers/entity.reducer';
import { WindowRefService } from '../../../../services/window-ref.service';
import { ShareOptions } from '../../../../models/MetaData';
import { truncateString } from 'core/utils/string-helper';

@Component({
  selector: 'app-rollup-card',
  templateUrl: './rollup-card.component.html',
  styleUrls: ['./rollup-card.component.scss'],
})
export class RollupCardComponent implements OnInit, OnDestroy {
  @Input() public formatGroup: FormatGroup;
  @Input() public bulkFeatureKey: BulkFeatureKey;
  @Input() public isListView?: boolean;
  @Output() public getIt$ = new EventEmitter<{ item: GetItRequest }>(true);
  @Output() public placeHold$ = new EventEmitter<{ item: HoldRequest }>(true);
  @ViewChild('searchCard', { static: true }) public searchCard: ElementRef;

  public queryParams = {};
  public subscription = new Subscription();
  public notFoundWords: string[] = [];
  public ariaTitleId: string;
  public entityTypes = EntityTypes;
  public resourceData: ShareItResource;
  public editionsData: EditionData[];
  public coverConfig: Cover;
  public seriesTitle: string;
  public seriesTitleSearch: string
  public language: string;
  public lockedFacetsQueryParams: FacetURLFilters;
  public originalSearchQueryValue: string;
  public originalSearchType: string;
  public readonly booksIcon = faBooks;
  public selectedTabName: string;
  public selectedTabRecordId: string;
  public author: string;
  public title: string;
  public shareData: ShareOptions;
  public materialType: string;
  public individualResultOnSrp: boolean
  public isWiderThanMd: boolean;
  public isBulkFeaturingEnabled = false;
  public isTabBasedMetadataEnabled = false;
  public selectedTabDescription: string;
  // TODO: re-think these flags and see if the HTML logic can be simplified when implementing work for: VDIS-34645
  public isCitationMailingEnabled: boolean;
  public isCitationMailingConfigured: boolean;

  public constructor(
    private readonly store: Store,
    private readonly i18nService: I18NService,
    private readonly router: Router,
    private readonly queryHelper: QueryHelper,
    private readonly route: ActivatedRoute,
    private readonly dictionaryService: DictionariesService,
    private readonly discoverV2BffService: DiscoverV2BffService,
    private readonly viewportService: ViewportService,
    private readonly featureToggleService: FeatureToggleService,
    private readonly customerFeatureToggleService: CustomerFeatureToggleService,
    private readonly windowRefService: WindowRefService
  ) {}

  public ngOnInit(): void {
    this.language = this.i18nService.getLangFacetTitle(this.formatGroup.language);

    this.isTabBasedMetadataEnabled = this.featureToggleService.getToggles()['VEGAENG-190_2026-01-31_tab_based_search_metadata'];

    if (!this.isTabBasedMetadataEnabled) {
      this.coverConfig = {
        coverUrl: this.formatGroup.coverUrl,
      };
      this.seriesTitle = this.formatGroup.seriesTitle;
      this.seriesTitleSearch = this.formatGroup.seriesTitleSearch;
      this.materialType = this.dictionaryService.getFormatDescriptionByCode(this.formatGroup?.materialTabs?.[0]?.materialTypes?.[0]);
    }

    this.isBulkFeaturingEnabled = this.customerFeatureToggleService.isAnyFeatureAvailable([CustomerFeature.BulkHolds, CustomerFeature.BulkBookmarks]);
    this.isCitationMailingEnabled = this.featureToggleService.getToggles()['VDIS-29183_2025-08-30_citation-sharing'];

    this.subscription.add(this.store.pipe(select(getSearchResultsMetadataForScroll)).subscribe(() => {
      this.setFocusOnSelectedCard();
    }));

    this.subscription.add(this.store.pipe(select(getSelectedRollupTabIndex(this.formatGroup.id))).subscribe((selectedTabIndex) => {
      if (this.formatGroup?.materialTabs?.length) {
        this.extractSelectedTabData(selectedTabIndex);
      }
    }));
    this.subscription.add(this.discoverV2BffService.$page.subscribe((page) => {
      if (page) {
        this.individualResultOnSrp = page.settings?.social_media_display?.individual_result_on_srp;
        this.isCitationMailingConfigured = page.settings?.citation_sharing?.individual_result_on_srp;
      }
    }));
    this.subscription.add(this.store.pipe(select(getLockedFacetsAsQueryParams)).subscribe((result) => {
      this.lockedFacetsQueryParams = result;
    }));

    this.subscription.add(this.route.queryParamMap.subscribe((params) => {
      this.originalSearchQueryValue = params.get('query');
      this.originalSearchType = params.get('searchType');
    }));

    this.notFoundWords = this.formatGroup && this.formatGroup.missingWords|| [];

    this.queryParams = {
      id: this.formatGroup.id,
      entityType: this.formatGroup.entityType,
    };
    this.ariaTitleId = `ariaTitleRollupCardId-${this.formatGroup.id}`;
    this.title = this.formatGroup?.title;
    this.author = this.formatGroup?.primaryAgent?.label;

    this.subscription.add(
      this.viewportService.isWiderThan(Breakpoints.RESOLUTION_MAX_MD).subscribe((isWiderThanMd) => {
        this.isWiderThanMd = isWiderThanMd;
      })
    );

    this.shareTheLink();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public onSaveFocus(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.type === 'click') {
      this.store.dispatch(setSelectedResourceId({resourceId: this.formatGroup.id}));
    }
  }

  public onSeriesTypeSearch(event: KeyboardEvent, seriesTitle: string, seriesTitleSearch: string) {
    const searchText = `"${(seriesTitleSearch || seriesTitle).replace(/"/g, '')}"`;
    const searchType = SearchType.SERIES;
    if ((event.key === 'Enter' || event.type === 'click') &&
      (this.originalSearchQueryValue !== searchText || searchType !== this.originalSearchType)) {
      this.store.dispatch(resetScrollAndSelectedResourceId());
      this.store.dispatch(new SearchResultsReset());
      this.store.dispatch(new UpdateSearchObjectAction({searchParams: {type: {searchText: searchText }}, searchType}));
      this.store.dispatch(new ResetFacetBubblesAction({}));
      const queryParams = this.queryHelper.prepareQueryParams(searchText, this.lockedFacetsQueryParams, searchType);
      this.router.navigate(['/search'], queryParams);
    }
  }

  public extractSelectedTabData(selectedTabIndex: number) {
    const selectedTab = this.formatGroup.materialTabs?.[selectedTabIndex];
    this.editionsData = selectedTab?.editions?.map((edition: Edition) => ({
      instanceId: edition.id,
      editionStatement: [edition.edition],
      publicationDate: edition.publicationDate,
      recordId: edition.recordId,
    } as EditionData));
    this.selectedTabName = selectedTab?.name;
    this.selectedTabRecordId = selectedTab?.editions?.[0]?.recordId;
    this.formatGroup.selectedTabRecordId = this.selectedTabRecordId;

    if (this.isTabBasedMetadataEnabled) {
      this.coverConfig = {
        coverUrl: selectedTab?.coverUrl,
      };
      this.seriesTitle = selectedTab?.seriesTitle;
      this.seriesTitleSearch = selectedTab?.seriesTitleSearch;
      this.materialType = this.dictionaryService.getFormatDescriptionByCode(selectedTab?.materialTypes?.[0]);
      this.selectedTabDescription = selectedTab?.description;
    }

    this.resourceData = {
      type: ShareItShareEntityType.RESOURCE,
      attributes: {
        id: this.formatGroup.id,
        url: `/search/card?recordId=${this.selectedTabRecordId}`,
        title: this.formatGroup.title,
        author: this.formatGroup.primaryAgent?.label || '',
        publicationDate: this.formatGroup.publicationDate || '',
        resourceType: this.formatGroup.entityType,
      }
    };

    this.shareTheLink();
  }

  public setFocusOnSelectedCard() {
    this.subscription.add(
      merge(
        this.store.pipe(select(getSelectedResourceId)).pipe(map((resourceId) => [resourceId, false])),
        this.store.pipe(select(getFocusedResourceId)).pipe(map((resourceId) => [resourceId, true])),
      ).subscribe(([resourceId, unsetFocus]) => {
        if (resourceId === this.formatGroup.id) {
          setTimeout(() => {
            this.searchCard.nativeElement.focus();
            this.searchCard.nativeElement.scrollIntoView();

            if (unsetFocus) {
              this.store.dispatch(resetFocusedResourceId());
            }
          }, 0);
        }
      }));
  }

  public shareTheLink() {
    const { attributes } = this.resourceData;
    const { title, url, id } = attributes;
    const { coverUrl } = this.coverConfig;

    const image = coverUrl?.['large'] || coverUrl?.['medium'] || coverUrl?.['small'] || '';
    const origin = this.windowRefService.origin() + url;
    const description = this.isTabBasedMetadataEnabled && this.selectedTabDescription
      ? encodeURIComponent(truncateString(this.selectedTabDescription, 150))
      : '';

    this.shareData = {
      id,
      author: encodeURIComponent(this.author),
      materialType: encodeURIComponent(this.materialType),
      title: encodeURIComponent(truncateString(title, 50)),
      description: description,
      image: image,
      url: origin
    };
  }
}
